import React from 'react'

const ID_COUNTER = { v: 0 }
function useId(prefix: string) {
  return React.useMemo(() => `${prefix}-${(++ID_COUNTER.v).toString(36)}`, [prefix])
}

export function Sparkline({
  data, color = 'var(--accent)', height = 28, width = 120, fill = true,
}: { data: number[]; color?: string; height?: number; width?: number; fill?: boolean }) {
  const id = useId('sg')
  if (!data.length) return <svg className="spark" viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height }} />
  const max = Math.max(...data), min = Math.min(...data)
  const r = max - min || 1
  const w = width, h = height
  const pts = data.map((v, i) => [
    (i / Math.max(1, data.length - 1)) * w,
    h - ((v - min) / r) * (h - 4) - 2,
  ])
  const path = pts.map((p, i) => (i ? 'L' : 'M') + p[0].toFixed(1) + ',' + p[1].toFixed(1)).join(' ')
  const area = `${path} L${w},${h} L0,${h} Z`
  return (
    <svg className="spark" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ width: '100%', height }}>
      {fill && (
        <>
          <defs>
            <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity="0.25" />
              <stop offset="100%" stopColor={color} stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={area} fill={`url(#${id})`} />
        </>
      )}
      <path d={path} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  )
}

export function AreaChart({
  data, color = 'var(--accent)', height = 140, gridY = 3, yLabels, xLabels,
}: {
  data: number[]
  color?: string
  height?: number
  gridY?: number
  yLabels?: (string | number)[]
  xLabels?: string[]
}) {
  const id = useId('ac')
  if (!data.length) return <svg className="chart" viewBox={`0 0 600 ${height}`} style={{ width: '100%', height }} />
  const max = Math.max(...data), min = 0
  const r = max - min || 1
  const w = 600, h = height
  const pad = { l: 36, r: 8, t: 8, b: 18 }
  const cw = w - pad.l - pad.r
  const ch = h - pad.t - pad.b
  const pts = data.map((v, i) => [
    pad.l + (i / Math.max(1, data.length - 1)) * cw,
    pad.t + ch - ((v - min) / r) * ch,
  ])
  const path = pts.map((p, i) => (i ? 'L' : 'M') + p[0].toFixed(1) + ',' + p[1].toFixed(1)).join(' ')
  const area = `${path} L${pad.l + cw},${pad.t + ch} L${pad.l},${pad.t + ch} Z`
  return (
    <svg className="chart" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ width: '100%', height }}>
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.28" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      {Array.from({ length: gridY + 1 }).map((_, i) => {
        const y = pad.t + (i / gridY) * ch
        const v = max - (i / gridY) * r
        return (
          <g key={i}>
            <line x1={pad.l} x2={w - pad.r} y1={y} y2={y} stroke="var(--border)" strokeDasharray="2 3" />
            <text x={pad.l - 6} y={y + 3} fill="var(--text-dim)" fontSize="9" fontFamily="var(--font-mono)" textAnchor="end">
              {yLabels ? yLabels[i] : Math.round(v)}
            </text>
          </g>
        )
      })}
      <path d={area} fill={`url(#${id})`} />
      <path d={path} fill="none" stroke={color} strokeWidth="1.6" strokeLinejoin="round" />
      {xLabels && xLabels.map((l, i) => (
        <text
          key={i}
          x={pad.l + (i / Math.max(1, xLabels.length - 1)) * cw}
          y={h - 4}
          fill="var(--text-dim)"
          fontSize="9"
          fontFamily="var(--font-mono)"
          textAnchor={i === 0 ? 'start' : i === xLabels.length - 1 ? 'end' : 'middle'}
        >
          {l}
        </text>
      ))}
    </svg>
  )
}

export function Bars({
  data, height = 70, color = 'var(--accent)',
}: { data: number[]; height?: number; color?: string; labels?: string[] }) {
  if (!data.length) return <svg className="chart" viewBox={`0 0 200 ${height}`} style={{ width: '100%', height }} />
  const max = Math.max(...data) || 1
  const w = 200, h = height, pad = 4
  const bw = (w - pad * 2) / data.length - 2
  return (
    <svg className="chart" viewBox={`0 0 ${w} ${h}`} style={{ width: '100%', height }}>
      {data.map((v, i) => {
        const bh = (v / max) * (h - 12)
        return (
          <rect
            key={i}
            x={pad + i * (bw + 2)}
            y={h - bh - 2}
            width={bw}
            height={bh}
            fill={color}
            opacity={0.9}
            rx="1.5"
          />
        )
      })}
    </svg>
  )
}

export type DonutSegment = { value: number; color: string; label?: string }

export function Donut({ segments, size = 84 }: { segments: DonutSegment[]; size?: number }) {
  const total = segments.reduce((a, s) => a + s.value, 0) || 1
  const r = size / 2 - 8
  const cx = size / 2, cy = size / 2
  let acc = 0
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--border)" strokeWidth="6" />
      {segments.map((s, i) => {
        const frac = s.value / total
        const len = 2 * Math.PI * r
        const dash = `${frac * len} ${len}`
        const off = -acc * len
        acc += frac
        return (
          <circle
            key={i}
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke={s.color}
            strokeWidth="6"
            strokeDasharray={dash}
            strokeDashoffset={off}
            transform={`rotate(-90 ${cx} ${cy})`}
          />
        )
      })}
    </svg>
  )
}
